/**
 * Online Status Hook
 *
 * Detects network connectivity and notifies user when offline/online
 * Shows toast notifications when connection status changes
 */
import { useEffect, useState } from 'react';
import { useNotification } from '@/shared/utils/notification';
import { logger } from '@/shared/utils/logger';

export interface OnlineStatusOptions {
  showNotifications?: boolean;
  onOnline?: () => void;
  onOffline?: () => void;
}

/**
 * Hook to monitor online/offline status
 * @param options Configuration options for notifications and callbacks
 * @returns Current online status
 */
export const useOnlineStatus = (options: OnlineStatusOptions = {}): boolean => {
  const { showNotifications = true, onOnline, onOffline } = options;

  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const { showToast } = useNotification();

  useEffect(() => {
    const handleOnline = (): void => {
      setIsOnline(true);
      logger.info('Network connection restored');

      if (showNotifications) {
        showToast({
          type: 'info',
          title: 'Back Online',
          message: 'Your internet connection has been restored.',
        });
      }

      onOnline?.();
    };

    const handleOffline = (): void => {
      setIsOnline(false);
      logger.warn('Network connection lost');

      if (showNotifications) {
        showToast({
          type: 'info',
          title: 'No Internet Connection',
          message: 'Please check your network connection and try again.',
        });
      }

      onOffline?.();
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [showNotifications, onOnline, onOffline, showToast]);

  return isOnline;
};

export default useOnlineStatus;
